import { IPromise, IScope } from "angular";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../../../../../records/forms/formView";
import { StateService } from "@uirouter/angularjs";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../../records/forms/formly/fieldConfigurationHelpers";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../../../records/utils";
import { RecordFileTemplateOptions } from "../../../../../../records/forms/formly/types";
import fieldHelpers from "../../../../../../ui-formly/fieldHelpers";
import { MachineConfigurationView } from "../../../../models/machineConfiguration";
import { cloneDeep } from "lodash";
import { IExpressionFunction, IFieldArray } from "AngularFormly";
import * as lookupNames from "../../../../configs/lookup";
import { LookupManagementService } from "../../../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName } from "../../../../../shared/lookups";
import { Record } from "../../../../../../records/server/crud";
import {
  ChangeJournalModalService,
  vsServiceName as ChangeJournalModalServiceName,
} from "../../../../../../records/changeJournal/changeNoteModalService";

interface IMultipleMoveFormModel {
  MachineChanges: IMachineMoveRequest[];
  Date?: Date;
  Files: File[];
  Notes?: string;
}

interface IMachineMoveRequest {
  MachineId: number;
  SlotNumber?: string;
  MachineSectionId?: number;
  MachineSection?: Record;
  Bank?: string;
  Position?: string;
}

export class MachinesMoveViewComponentController {
  /* bindings */
  machineConfigurations!: MachineConfigurationView[];
  /* end bindings */

  model!: IMultipleMoveFormModel;
  machineInfo!: {
    machineConfigurations: MachineConfigurationView[];
  };
  formFields!: IFieldArray;

  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    "$scope",
    RecordsUtilsServiceName,
    vsLookupManagementServiceName,
    ChangeJournalModalServiceName
  ];
  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    $scope: IScope,
    private readonly recordUtils: RecordsUtilsService,
    private readonly machineLookupManagementService: LookupManagementService,
    private readonly changeJournalModalService: ChangeJournalModalService
  ) {
    $scope.$on("copyDown", (e, modelClicked) => {
      e.preventDefault();
      this.copyDown(modelClicked);
    });
  }

  private initializeFormFields() {
    const fileTemplateOptions: RecordFileTemplateOptions = {
      allowMultiple: true,
      label: "Files",
    };

    this.formFields = [
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Date",
            name: "Date",
            label: "Change Date",
            required: true,
          }),
        ],
        4
      ),
      this.fieldConfigurationHelperService.repeaterField({
        key: "MachineChanges",
        name: "MachineChanges",
        allowAdd: false,
        allowAddBottom: false,
        displayNameSingular: "Machine",
        displayNamePlural: "Machines",
        repeaterFields: fieldHelpers.columnLayout(
          [
            fieldHelpers.simpleReadonlyField({
              key: "MachineId",
              className: "col-xs-3",
              label: "Serial Number",
              formatters: [
                (machineId) => {
                  const machineConfigurationView =
                    this.getMachineConfigurationFromMachineId(
                      <number>machineId
                    );
                  return machineConfigurationView?.SerialNumber;
                },
              ],
            }),
            fieldHelpers.simpleStringField({
              key: "SlotNumber",
              className: "col-xs-3",
              label: "Machine Number",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            this.fieldConfigurationHelperService.lookupField(
              this.machineLookupManagementService,
              {
                key: "MachineSectionId",
                label: "Section",
                required: true,
                objectName: lookupNames.sectionName,
                hideAddButton: true,
                expressionProperties: {
                  "templateOptions.helpText": this.previousText(
                    (v) => v?.MachineSectionName
                  ),
                },
              }
            ),
            fieldHelpers.simpleStringField({
              key: "Bank",
              label: "Bank",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            fieldHelpers.simpleStringField({
              key: "Position",
              label: "Position",
              required: true,
              expressionProperties: {
                "templateOptions.helpText": this.previousText(),
              },
            }),
            {
              key: "copyDown",
              name: "copyDown",
              className: "col-xs-3",
              templateOptions: {
                label: " ",
              },
              template: `
<div>
  <div class="pull-right">
    <div class="btn-group">
      <div class="btn btn-primary game-config-btn" ng-click="$emit('copyDown', model)">
        <i class="glyphicon glyphicon-arrow-down"></i>
      </div>
    </div>
  </div>
</div>
        `,
              wrapper: ["bootstrapLabel", "bootstrapHasError"],
            },
          ],
          4
        ),
        showHeader: true,
      }),
      {
        key: "Files",
        name: "Files",
        type: "recordFileUpload",
        templateOptions: fileTemplateOptions,
      },
      fieldHelpers.headerField({ headerText: "Notes", key: "NotesHeader" }),
      fieldHelpers.textAreaField({
        key: "Notes",
        name: "Notes",
        label: " ",
        rows: 5,
      }),
    ];
  }

  $onInit(): void {
    this.model = {
      Files: [],
      MachineChanges: (this.machineConfigurations ?? []).map((mc) => {
        return {
          MachineId: mc.MachineId,
          SlotNumber: <string | undefined>mc.SlotNumber,
          MachineSectionId: undefined,
          MachineSection: undefined,
          Bank: undefined,
          Slot: undefined,
        };
      }),
    };
    this.machineInfo = {
      machineConfigurations: cloneDeep(this.machineConfigurations),
    };
    this.initializeFormFields();
  }

  copyDown(modelClicked: IMachineMoveRequest) {
    const machineMoves = this.model.MachineChanges;
    const copyIndex = machineMoves.indexOf(modelClicked);
    for (let i = copyIndex + 1; i < machineMoves.length; i++) {
      const newModel: IMachineMoveRequest = {
        MachineId: machineMoves[i].MachineId,
        SlotNumber: machineMoves[i].SlotNumber,
        MachineSectionId: modelClicked.MachineSectionId,
        MachineSection: modelClicked.MachineSection,
        Bank: modelClicked.Bank,
      };
      this.recordUtils.assignId(newModel);
      machineMoves.splice(i, 1, newModel);
    }
  }

  private previousText(
    fieldSelector?: (
      machineConfigurationView: MachineConfigurationView | undefined
    ) => any
  ): IExpressionFunction {
    return (_, __, scope) => {
      const machineConfiguration = this.getMachineConfigurationFromMachineId(
        scope.model?.MachineId
      );

      const fieldKey = scope.options.key;
      fieldSelector ??= (view) => (view ? view[fieldKey] : undefined);

      const value = fieldSelector(machineConfiguration);
      return value ? `Previous: ${value}` : undefined;
    };
  }

  submit(formViewController: FormViewController): IPromise<void> {
    return this.changeJournalModalService
      .getAndSetChangeJournalNote(this.model)
      .then(() =>
        this.formViewService.submitForm({
          url: `/api/machines/move`,
          formObject: this.model,
          formViewController,
          method: "post",
        })
      )
      .then(() => {
        this.$state.go("^.grid", undefined, { reload: true });
      });
  }

  async cancel(): Promise<void> {
    await this.$state.go("^.grid", undefined, { reload: true });
  }

  private getMachineConfigurationFromMachineId(machineId: number) {
    return this.machineInfo.machineConfigurations.find(
      (mc) => mc.MachineId === machineId
    );
  }
}
